import React, { useEffect, useRef, useState } from 'react';
import './EventSlideViewer.css';

const EventSlideViewer = ({
  slides = [],
  expectedCount = 0,
  altPrefix = '슬라이드',
  followLatest = false,
  onOpen,
  className = '',
}) => {
  const filled = slides.filter(Boolean);
  const total = Math.max(filled.length, expectedCount, 1);
  const [index, setIndex] = useState(0);
  const touchStartXRef = useRef(null);

  useEffect(() => {
    if (!filled.length) {
      setIndex(0);
      return;
    }
    if (followLatest) {
      setIndex(filled.length - 1);
      return;
    }
    setIndex((current) => Math.min(current, filled.length - 1));
  }, [filled.length, followLatest]);

  const currentSrc = filled[index] || '';
  const canPrev = index > 0;
  const canNext = index < filled.length - 1;

  const goTo = (nextIndex) => {
    if (!filled.length) return;
    setIndex(Math.max(0, Math.min(filled.length - 1, nextIndex)));
  };

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.changedTouches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    const startX = touchStartXRef.current;
    const endX = event.changedTouches[0]?.clientX;
    touchStartXRef.current = null;
    if (startX == null || endX == null) return;
    const delta = endX - startX;
    if (delta > 40) goTo(index - 1);
    if (delta < -40) goTo(index + 1);
  };

  return (
    <div
      className={`event-slide-viewer ${className}`.trim()}
      data-count={filled.length}
      data-index={index}
    >
      <div
        className="event-slide-viewer__stage"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {currentSrc ? (
          onOpen ? (
            <button
              type="button"
              className="event-slide-viewer__open"
              onClick={() => onOpen(currentSrc, index)}
            >
              <img src={currentSrc} alt={`${altPrefix} ${index + 1}`} />
            </button>
          ) : (
            <img src={currentSrc} alt={`${altPrefix} ${index + 1}`} />
          )
        ) : (
          <span className="event-slide-viewer__skeleton" aria-hidden="true" />
        )}
      </div>
      {filled.length > 1 && (
        <div className="event-slide-viewer__nav">
          <button
            type="button"
            aria-label="이전 슬라이드"
            disabled={!canPrev}
            onClick={() => goTo(index - 1)}
          >
            이전
          </button>
          <span>{index + 1} / {filled.length}</span>
          <button
            type="button"
            aria-label="다음 슬라이드"
            disabled={!canNext}
            onClick={() => goTo(index + 1)}
          >
            다음
          </button>
        </div>
      )}
      {total > 1 && (
        <div className="event-slide-viewer__dots" aria-hidden="true">
          {Array.from({ length: total }).map((_, dotIndex) => (
            <i
              key={`dot-${dotIndex}`}
              className={dotIndex === index ? 'active' : ''}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EventSlideViewer;
